import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Bell,
  CheckCheck,
  Volume2,
  VolumeX,
  Target,
  FileText,
  CalendarClock,
  ReceiptText,
  Radio,
  CheckCircle2,
  Briefcase,
  RefreshCw,
  Mail,
  PhoneIncoming,
  type LucideIcon
} from 'lucide-react';
import { ROUTES } from '@/routes/paths';
import { api } from '@/shared/api/axios';
import { notificationRoute } from '@/features/activity/utils/notificationRoute';
import {
  playNotificationSound,
  unlockNotificationAudio,
  isSoundEnabled,
  setSoundEnabled,
  playSuccessSound
} from '@/shared/utils/sound';

export interface NotificationItem {
  id: string;
  title: string;
  description: string;
  time: string;
  category: 'sales' | 'quotations' | 'hr' | 'finance' | 'system' | 'general';
  unread: boolean;
  link?: string;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
}

interface RawNotification {
  id: string;
  type: string;
  message: string;
  is_read: boolean;
  related_entity_id?: string;
  related_entity_type?: string;
  created_at: string;
}

/**
 * Maps a real backend Notification (src/db-mongo.js's Notifications
 * collection — GET /api/notifications) to a deep-linked, iconed panel item.
 * This panel previously showed hardcoded demo rows plus a generic feed built
 * from /api/activity (an unrelated activity log with no per-user read state
 * or real target links) — genuine notifications created throughout the app
 * (invoice request approvals, deal assignments, meeting invites, HR
 * warnings...) never appeared here at all, which is why neither "a sound
 * plays when something real happens" nor "clicking goes to the right place"
 * ever actually worked.
 */
function mapNotification(n: RawNotification): NotificationItem {
  const t = n.type || '';
  let category: NotificationItem['category'] = 'general';
  let icon: LucideIcon = CheckCircle2;
  let iconColor = 'text-teal-600';
  let iconBg = 'bg-teal-50';
  let link: string = ROUTES.activity;

  if (t === 'invoice_credit_approval' || t === 'field_invoice_request') {
    // Waiting for an approver: the approvals queue, not the invoice list.
    category = 'finance';
    icon = ReceiptText;
    iconColor = 'text-amber-600';
    iconBg = 'bg-amber-50';
    link = ROUTES.fieldApprovals;
  } else if (t.startsWith('invoice_request') || t === 'invoice_credit_decision') {
    category = 'finance';
    icon = ReceiptText;
    iconColor = 'text-amber-600';
    iconBg = 'bg-amber-50';
    link = ROUTES.invoices;
  } else if (t.startsWith('deal')) {
    category = 'sales';
    icon = Briefcase;
    iconColor = 'text-blue-600';
    iconBg = 'bg-blue-50';
    link = n.related_entity_id
      ? ROUTES.dealDetail.replace(':id', n.related_entity_id)
      : ROUTES.deals;
  } else if (t.startsWith('lead')) {
    category = 'sales';
    icon = Target;
    iconColor = 'text-blue-600';
    iconBg = 'bg-blue-50';
    link = n.related_entity_id
      ? `${ROUTES.leads}?leadId=${encodeURIComponent(n.related_entity_id)}`
      : ROUTES.leads;
  } else if (t.startsWith('quotation') || t.startsWith('quote')) {
    category = 'quotations';
    icon = FileText;
    iconColor = 'text-indigo-600';
    iconBg = 'bg-indigo-50';
    link = n.related_entity_id
      ? ROUTES.quotationDetail.replace(':id', n.related_entity_id)
      : ROUTES.quotations;
  } else if (t === 'meeting_invite') {
    category = 'general';
    icon = CalendarClock;
    iconColor = 'text-emerald-600';
    iconBg = 'bg-emerald-50';
    link = n.related_entity_id
      ? `${ROUTES.meetings}?meetingId=${encodeURIComponent(n.related_entity_id)}`
      : ROUTES.meetings;
  } else if (t === 'project_assigned') {
    category = 'general';
    icon = Briefcase;
    iconColor = 'text-purple-600';
    iconBg = 'bg-purple-50';
    link = n.related_entity_id
      ? `${ROUTES.projects}?projectId=${encodeURIComponent(n.related_entity_id)}`
      : ROUTES.projects;
  } else if (t === 'task_assigned') {
    category = 'general';
    icon = CheckCircle2;
    iconColor = 'text-purple-600';
    iconBg = 'bg-purple-50';
    link = n.related_entity_id
      ? `${ROUTES.tasks}?taskId=${encodeURIComponent(n.related_entity_id)}`
      : ROUTES.tasks;
  } else if (t.startsWith('contact')) {
    // contact_inbound_call: "a client of yours just called a colleague". It used
    // to fall through to the default and open the Activity page, when the one
    // useful thing to do is open that contact and ring them back.
    category = 'sales';
    if (t === 'contact_inbound_call') {
      icon = PhoneIncoming;
      iconColor = 'text-amber-600';
      iconBg = 'bg-amber-50';
    }
    link = notificationRoute(n) || ROUTES.contacts;
  } else if (t === 'mail_received') {
    category = 'general';
    icon = Mail;
    iconColor = 'text-sky-600';
    iconBg = 'bg-sky-50';
    link = ROUTES.mailInbox;
  } else if (t.startsWith('incentive')) {
    // Was falling into the hr_*/warning_threshold bucket below and landing
    // on Attendance — a real but wrong page for an incentive notification.
    category = 'hr';
    icon = Briefcase;
    iconColor = 'text-emerald-600';
    iconBg = 'bg-emerald-50';
    link = ROUTES.incentives;
  } else if (t.startsWith('hr_') || t === 'warning_threshold') {
    category = 'hr';
    icon = CalendarClock;
    iconColor = 'text-emerald-600';
    iconBg = 'bg-emerald-50';
    link = ROUTES.attendance;
  } else if (t === 'security_lockout') {
    category = 'system';
    icon = Radio;
    iconColor = 'text-rose-600';
    iconBg = 'bg-rose-50';
  } else if (t === 'invoice_overdue' || t === 'invoice_escalated') {
    category = 'finance';
    icon = ReceiptText;
    iconColor = 'text-rose-600';
    iconBg = 'bg-rose-50';
    link = n.related_entity_id
      ? `${ROUTES.accountsSales}?invoiceId=${encodeURIComponent(n.related_entity_id)}`
      : ROUTES.accountsSales;
  } else if (t === 'bill_due_soon') {
    category = 'finance';
    icon = ReceiptText;
    iconColor = 'text-amber-600';
    iconBg = 'bg-amber-50';
    link = n.related_entity_id
      ? `${ROUTES.accountsPurchases}?billId=${encodeURIComponent(n.related_entity_id)}`
      : ROUTES.accountsPurchases;
  } else if (t === 'budget_exceeded') {
    category = 'finance';
    icon = ReceiptText;
    iconColor = 'text-rose-600';
    iconBg = 'bg-rose-50';
    link = ROUTES.accountsBudgets;
  } else if (t === 'low_cash_balance') {
    category = 'finance';
    icon = ReceiptText;
    iconColor = 'text-rose-600';
    iconBg = 'bg-rose-50';
    link = ROUTES.bankingReconciliation;
  } else if (t === 'payment_received') {
    category = 'finance';
    icon = ReceiptText;
    iconColor = 'text-emerald-600';
    iconBg = 'bg-emerald-50';
    link = n.related_entity_id
      ? ROUTES.dealDetail.replace(':id', n.related_entity_id)
      : ROUTES.deals;
  } else if (t === 'approval_pending' || t === 'approval_decision') {
    category = 'finance';
    icon = ReceiptText;
    iconColor = 'text-amber-600';
    iconBg = 'bg-amber-50';
    link = ROUTES.accountingV2Approvals;
  }

  if (t.startsWith('followup') || t.startsWith('appointment') || t === 'follow_up_set') {
    icon = CalendarClock;
    link = notificationRoute(n) || ROUTES.activity;
  }

  // Every notification about one record opens THAT record (client, 2026-10-06):
  // a link that names the record wins over the section-level one above (e.g. a
  // lead notification used to land on the whole pipeline).
  if (n.related_entity_id) {
    const specific = notificationRoute(n as Parameters<typeof notificationRoute>[0]);
    if (specific && specific.includes(encodeURIComponent(n.related_entity_id))) link = specific;
  }

  return {
    id: n.id,
    title: t ? t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ') : 'Notification',
    description: n.message || '',
    time: n.created_at ? formatTimeAgo(n.created_at) : 'Just now',
    category,
    unread: !n.is_read,
    link,
    icon,
    iconColor,
    iconBg,
  };
}

function formatTimeAgo(dateStr: string): string {
  try {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return 'Recently';
  }
}

const POLL_INTERVAL_MS = 5_000;

export function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [soundOn, setSoundOn] = useState(isSoundEnabled);
  const [soundTested, setSoundTested] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Tracks ids already seen so the chime only fires for genuinely NEW
  // notifications on a poll, not every unread one re-fetched from the server.
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);
  const fetchingRef = useRef(false);

  const unreadCount = notifications.filter((n) => n.unread).length;

  const fetchNotifications = useCallback(async (chimeOnNew = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      setLoading(true);
      const res = await api.get<{ notifications: RawNotification[] }>('/api/notifications?limit=30');
      const rows = Array.isArray(res.data?.notifications) ? res.data.notifications : [];
      const items = rows.map(mapNotification);

      const isFirstLoad = !initializedRef.current;
      const freshUnread = items.filter((it) => it.unread && !seenIdsRef.current.has(it.id));
      seenIdsRef.current = new Set(items.map(it => it.id));
      initializedRef.current = true;

      // Never chime on the very first load — that would fire for every
      // pre-existing unread notification the moment the app opens, not just
      // ones that actually just arrived.
      if (chimeOnNew && !isFirstLoad && freshUnread.length > 0) {
        playNotificationSound();
        freshUnread.slice(0, 3).forEach(item => toast(t => (
          <button type="button" className="block max-w-[270px] text-left" onClick={() => {
            toast.dismiss(t.id);
            if (item.link) navigate(item.link);
          }}>
            <strong className="block text-sm">{item.title}</strong>
            <span className="mt-0.5 block text-sm leading-5 line-clamp-2">{item.description}</span>
          </button>
        ), { id: `notification-${item.id}`, duration: 8000, icon: '🔔' }));
      }

      setNotifications(items);
    } catch {
      // Offline / unauthenticated — leave whatever was last loaded in place.
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    document.addEventListener('pointerdown', unlockNotificationAudio);
    document.addEventListener('keydown', unlockNotificationAudio);
    return () => {
      document.removeEventListener('pointerdown', unlockNotificationAudio);
      document.removeEventListener('keydown', unlockNotificationAudio);
    };
  }, []);

  useEffect(() => {
    fetchNotifications(false);
    // Poll so a notification created while the tab is open is actually
    // noticed — without this, the chime only ever fired on manual bell
    // clicks, which defeats the point of a notification sound.
    const interval = setInterval(() => fetchNotifications(true), POLL_INTERVAL_MS);
    const stream = new EventSource('/api/notifications/stream', { withCredentials: true });
    stream.addEventListener('notification', () => fetchNotifications(true));
    return () => { clearInterval(interval); stream.close(); };
  }, [fetchNotifications]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleToggleOpen = () => {
    const nextState = !open;
    setOpen(nextState);
    if (nextState) {
      fetchNotifications(true);
    }
  };

  const handleTestSound = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSoundTested(true);
    playNotificationSound(true); // force play even if muted to test
    setTimeout(() => setSoundTested(false), 800);
  };

  const handleToggleSound = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextVal = !soundOn;
    setSoundOn(nextVal);
    setSoundEnabled(nextVal);
    if (nextVal) {
      playSuccessSound();
    }
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
    playSuccessSound();
    try {
      await api.post('/api/notifications/read-all');
    } catch {
      // Local state already reflects "read"; a failed sync here just means
      // the next poll may show them unread again, which is a safe fallback.
    }
  };

  const handleItemClick = (item: NotificationItem) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === item.id ? { ...n, unread: false } : n)),
    );
    setOpen(false);
    if (item.unread) {
      api.patch(`/api/notifications/${item.id}/read`).catch(() => {});
    }
    if (item.link) {
      navigate(item.link);
    }
  };

  const displayedNotifications =
    filter === 'unread' ? notifications.filter((n) => n.unread) : notifications;

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* Notification Bell Trigger Button (Opens in-place dropdown, NOT navigation) */}
      <button
        type="button"
        onClick={handleToggleOpen}
        aria-label="Notifications"
        aria-expanded={open}
        title="In-App Notifications & Alerts"
        className="relative p-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        <Bell className={`w-4 h-4 transition-transform ${open ? 'scale-110 text-blue-600' : ''}`} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] font-black flex items-center justify-center border-2 border-white shadow-xs animate-in zoom-in-50">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Flyout In-App Notification Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="In-App Notifications Panel"
          className="absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl bg-white border border-slate-200/90 shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/80">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-slate-900">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-[10px] font-extrabold rounded-full bg-blue-100 text-blue-700">
                  {unreadCount} new
                </span>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Test Audio Chime Sound */}
              <button
                type="button"
                onClick={handleTestSound}
                title="Test notification chime sound"
                className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition ${
                  soundTested
                    ? 'bg-blue-100 text-blue-700 scale-105'
                    : 'text-slate-500 hover:bg-slate-200/70 hover:text-slate-800'
                }`}
              >
                <Volume2 className="w-3.5 h-3.5" />
                <span className="text-[11px] hidden sm:inline">Chime</span>
              </button>

              {/* Sound Toggle Button */}
              <button
                type="button"
                onClick={handleToggleSound}
                title={soundOn ? 'Mute notification sound' : 'Enable notification sound'}
                className={`p-1.5 rounded-lg text-xs font-semibold transition ${
                  soundOn
                    ? 'text-emerald-700 hover:bg-emerald-50'
                    : 'text-slate-400 hover:bg-slate-100'
                }`}
              >
                {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              </button>

              {/* Mark All Read */}
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  title="Mark all as read"
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200/70 hover:text-slate-800 transition"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs & Refresh */}
          <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-100 text-xs bg-white">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={`px-2.5 py-1 rounded-md font-semibold transition ${
                  filter === 'all'
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                All ({notifications.length})
              </button>
              <button
                type="button"
                onClick={() => setFilter('unread')}
                className={`px-2.5 py-1 rounded-md font-semibold transition ${
                  filter === 'unread'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Unread ({unreadCount})
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fetchNotifications(false)}
                title="Refresh notifications"
                className="text-slate-400 hover:text-slate-700 p-1"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin text-blue-600' : ''}`} />
              </button>
            </div>
          </div>

          {/* Notifications List (Direct in-app items) */}
          <div className="max-h-[350px] overflow-y-auto divide-y divide-slate-100">
            {displayedNotifications.length === 0 ? (
              <div className="py-10 text-center px-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2 opacity-80" />
                <p className="text-xs font-semibold text-slate-700">All caught up!</p>
                <p className="text-[11px] text-slate-400 mt-0.5">No unread notifications right now.</p>
              </div>
            ) : (
              displayedNotifications.map((n) => {
                const IconComponent = n.icon;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleItemClick(n)}
                    className={`flex items-start gap-3 p-3.5 cursor-pointer transition text-left select-none ${
                      n.unread
                        ? 'bg-blue-50/30 hover:bg-blue-50/70'
                        : 'bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div className={`p-2 rounded-xl shrink-0 ${n.iconBg} ${n.iconColor}`}>
                      <IconComponent className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className={`text-xs ${n.unread ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                          {n.title}
                        </span>
                        <span className="text-[10px] text-slate-400 shrink-0">{n.time}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">
                        {n.description}
                      </p>
                    </div>

                    {n.unread && (
                      <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0 mt-1.5" />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Info */}
          <div className="p-2.5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-xs">
            <span className="text-[11px] text-slate-500 flex items-center gap-1">
              <span>Chime Audio:</span>
              <span className={`font-bold ${soundOn ? 'text-emerald-600' : 'text-slate-400'}`}>
                {soundOn ? 'Active' : 'Muted'}
              </span>
            </span>

            <span className="text-[11px] text-slate-400">
              Click item to view record
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
